import "./Users.css";
import React, {useCallback, useState} from "react";
import {DataLoadingStatus, Role} from "../../../Utils/enums";
import {createUser, deleteUser, demoteUser, getUsers, promoteUser} from "../../../Utils/queries";
import {User} from "../../../Utils/response-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEye, faEyeSlash} from "@fortawesome/free-regular-svg-icons";
import {get_login_validation_errors, get_password_validation_errors} from "../../../Utils/validation";

const UsersPage = ({role, myUserId}: {role: Role, myUserId: number}) => {
    const [users, setUsers] = useState<User[]>([]);
    const [status, setStatus] = useState<DataLoadingStatus>(DataLoadingStatus.NotLoaded);
    const [login, setLogin] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);

    const form_is_unfilled = login === "" || password === "";
    const login_err = get_login_validation_errors(login);
    const pwd_err = get_password_validation_errors(password);

    if (status === DataLoadingStatus.NotLoaded) {
        setStatus(DataLoadingStatus.Loading);
        getUsers().then(resp => {
            if (resp.errorCode === undefined) {
                setUsers(resp.users);
                setStatus(DataLoadingStatus.Loaded);
            } else {
                setStatus(DataLoadingStatus.Error);
            }
        });
    }

    const onCreateUser = useCallback(async ()=> {
        setStatus(DataLoadingStatus.Loading);
        createUser(login, password).then(resp => {
            if (resp.errorCode !== undefined || !resp.success) {
                alert(resp.message || `Error: ${resp.errorCode}`);
                setStatus(DataLoadingStatus.Error);
            } else {
                alert(resp.message);
                if (resp.success) {
                    setUsers(resp.users!);
                    setLogin("");
                    setPassword("");
                }
                setStatus(DataLoadingStatus.Loaded);
            }
        });
    }, [login, password, setUsers, setStatus]);
    
    return (
        <div className="users-main">
            <h2 className="users-heading">Admin Panel</h2>

            <div className="create-user">
                <input type="text" id="newLogin" placeholder="Login"
                       value={login} disabled={status === DataLoadingStatus.Loading}
                       onChange={(event) => {
                           setLogin(event.target.value);
                       }}/>
                <input type={showPassword ? 'text' : 'password'} id="newPassword" placeholder="Password"
                       value={password} disabled={status === DataLoadingStatus.Loading}
                       onChange={(event) => {
                           setPassword(event.target.value);
                       }}/>
                <p className="show-password" onClick={() => setShowPassword(prev => !prev)}>
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="form-icon"/>
                </p>
                <button id="createUserBtn" disabled={form_is_unfilled || !!login_err || !!pwd_err || status === DataLoadingStatus.Loading} onClick={onCreateUser}>Create User</button>
            </div>

            <p className="info-text-mini">
                { status === DataLoadingStatus.Loading ? "Waiting for response..." : "" }
            </p>

            <table id="usersTable">
                <thead>
                <tr>
                    <th>User ID</th>
                    <th>Login</th>
                    <th>Role</th>
                    <th>Last IP</th>
                    <th>Actions</th>
                </tr>
                </thead>
                <tbody>{
                    users.map((usr) => <UserRow key={usr.user_id} user={usr} myRole={role}
                                                myUserId={myUserId} setUsers={setUsers} setStatus={setStatus}/>)
                }</tbody>
            </table>
        </div>
    );
};

const UserRow = ({user, myRole, myUserId, setUsers, setStatus}: {
    user: User,
    myRole: Role,
    myUserId: number,
    setUsers: React.Dispatch<React.SetStateAction<User[]>>,
    setStatus: React.Dispatch<React.SetStateAction<DataLoadingStatus>>
}) => {
    const updateRole = useCallback(async () => {
        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to promote this user? You need to be superadmin to undone this action!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            promoteUser(user.user_id).then(resp => {
                if (resp.errorCode !== undefined || !resp.success) {
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setStatus(DataLoadingStatus.Error);
                } else {
                    alert(resp.message);
                    if (resp.success) setUsers(resp.users!);
                    setStatus(DataLoadingStatus.Loaded);
                }
            });
        }
    }, [user, setUsers, setStatus]);

    const demoteRole = useCallback(async ()=> {
        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to demote this user?");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            demoteUser(user.user_id).then(resp => {
                if (resp.errorCode !== undefined || resp.success) {
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setStatus(DataLoadingStatus.Error);
                } else {
                    alert(resp.message);
                    if (resp.success) setUsers(resp.users!);
                    setStatus(DataLoadingStatus.Loaded);
                }
            });
        }
    }, [user, setUsers, setStatus]);

    const removeUser = useCallback(async ()=> {
        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to delete this user and all their data? This action cannot be undone!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            deleteUser(user.user_id).then(resp => {
                if (resp.errorCode !== undefined || resp.success) {
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setStatus(DataLoadingStatus.Error);
                } else {
                    alert(resp.message);
                    if (resp.success) setUsers(resp.users!);
                    setStatus(DataLoadingStatus.Loaded);
                }
            });
        }
    }, [user, setUsers, setStatus]);

    return (
        <tr>
            <td>{user.user_id}</td>
            <td>{user.login}{user.user_id === myUserId ? ' (me)' : ''}</td>
            <td>{Role[user.role]}</td>
            <td>{user.last_ip ?? ''}</td>
            <td className="user-actions-td">{ (user.user_id !== myUserId &&
                <>
                    { user.role !== Role.SuperAdmin && (myRole.valueOf() > user.role.valueOf()) &&
                        <button className='action-btn promote' onClick={updateRole}>Promote</button>
                    }
                    { myRole === Role.SuperAdmin && user.role !== Role.User &&
                        <button className='action-btn demote' onClick={demoteRole}>Demote</button>
                    }
                    { (myRole === Role.SuperAdmin || myRole.valueOf() > user.role.valueOf()) &&
                        <button className='action-btn delete' onClick={removeUser}>Delete</button>
                    }
                </>) || <p style={{margin: 0}}>No actions available</p>
            }</td>
        </tr>
    );
}

export default UsersPage;